export const SITE_TIMEZONE = 'Europe/Zurich';

export type SiteDateTimeParts = {
  year: number;
  month: number;
  day: number;
  hours: number;
  minutes: number;
};

type SiteTimeZoneOption = {
  id: string;
  timeZone?: string | null;
};

type InternalSiteDateTimeParts = SiteDateTimeParts & { seconds: number };

const formatterCache = new Map<string, Intl.DateTimeFormat>();

const TWO_HOURS_MS = 2 * 60 * 60 * 1000;
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;
const ONE_MINUTE_MS = 60 * 1000;

function getCachedFormatter(
  cacheKey: string,
  factory: () => Intl.DateTimeFormat,
): Intl.DateTimeFormat {
  const cached = formatterCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  const formatter = factory();
  formatterCache.set(cacheKey, formatter);
  return formatter;
}

function getPartsFormatter(timeZone: string): Intl.DateTimeFormat {
  return getCachedFormatter(
    `parts:${timeZone}`,
    () =>
      new Intl.DateTimeFormat('en-CA', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
        hourCycle: 'h23',
      }),
  );
}

function normalizeLocale(locale: string | undefined): string {
  return locale && locale.trim() ? locale : 'default';
}

function getLocalizedFormatter(
  kind: 'label' | 'time',
  locale: string | undefined,
  timeZone: string,
): Intl.DateTimeFormat {
  const normalizedLocale = normalizeLocale(locale);
  return getCachedFormatter(
    `${kind}:${normalizedLocale}:${timeZone}`,
    () =>
      new Intl.DateTimeFormat(normalizedLocale === 'default' ? undefined : normalizedLocale, {
        timeZone,
        ...(kind === 'label'
          ? { dateStyle: 'medium', timeStyle: 'short' }
          : { hour: '2-digit', minute: '2-digit' }),
      }),
  );
}

function readPart(parts: Intl.DateTimeFormatPart[], type: Intl.DateTimeFormatPartTypes): number {
  const value = parts.find((part) => part.type === type)?.value ?? '0';
  const parsed = Number.parseInt(value, 10);
  return Number.isNaN(parsed) ? 0 : parsed;
}

function parseSitePartsFromInstant(instantMs: number, timeZone: string): InternalSiteDateTimeParts {
  const parts = getPartsFormatter(timeZone).formatToParts(new Date(instantMs));
  return {
    year: readPart(parts, 'year'),
    month: readPart(parts, 'month'),
    day: readPart(parts, 'day'),
    hours: readPart(parts, 'hour'),
    minutes: readPart(parts, 'minute'),
    seconds: readPart(parts, 'second'),
  };
}

function isValidInstantIso(iso: string): boolean {
  const parsed = new Date(iso);
  return !Number.isNaN(parsed.valueOf());
}

export function resolveSiteTimeZone(
  siteId: string | null | undefined,
  siteOptions: ReadonlyArray<SiteTimeZoneOption> | null | undefined,
  fallback = SITE_TIMEZONE,
): string {
  if (!siteId) {
    return fallback;
  }

  const matchedSite = siteOptions?.find((option) => option.id === siteId);
  const timeZone = matchedSite?.timeZone?.trim();
  return timeZone && timeZone.length > 0 ? timeZone : fallback;
}

function toComparableMinuteStamp(parts: SiteDateTimeParts): number {
  return Math.floor(
    Date.UTC(parts.year, parts.month - 1, parts.day, parts.hours, parts.minutes, 0, 0) /
      ONE_MINUTE_MS,
  );
}

function getOffsetMsAtInstant(instantMs: number, timeZone: string): number {
  const parts = parseSitePartsFromInstant(instantMs, timeZone);
  const asUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hours,
    parts.minutes,
    parts.seconds,
    0,
  );
  return asUtc - instantMs;
}

function sitePartsMatch(instantMs: number, target: SiteDateTimeParts, timeZone: string): boolean {
  const parts = parseSitePartsFromInstant(instantMs, timeZone);
  return (
    parts.year === target.year &&
    parts.month === target.month &&
    parts.day === target.day &&
    parts.hours === target.hours &&
    parts.minutes === target.minutes
  );
}

function getExactCandidateInstants(target: SiteDateTimeParts, timeZone: string): number[] {
  const utcGuess = Date.UTC(
    target.year,
    target.month - 1,
    target.day,
    target.hours,
    target.minutes,
    0,
    0,
  );
  const offsets = new Set([
    getOffsetMsAtInstant(utcGuess, timeZone),
    getOffsetMsAtInstant(utcGuess - TWO_HOURS_MS, timeZone),
    getOffsetMsAtInstant(utcGuess + TWO_HOURS_MS, timeZone),
  ]);

  const candidates = [...offsets]
    .map((offsetMs) => utcGuess - offsetMs)
    .filter((candidateMs) => sitePartsMatch(candidateMs, target, timeZone))
    .sort((a, b) => a - b);

  return [...new Set(candidates)];
}

function chooseClosestInstant(candidates: number[], referenceInstantMs?: number): number {
  if (!candidates.length) {
    throw new Error('Expected at least one candidate instant');
  }

  if (referenceInstantMs === undefined || Number.isNaN(referenceInstantMs)) {
    return candidates[0];
  }

  return candidates.reduce((best, current) => {
    const bestDistance = Math.abs(best - referenceInstantMs);
    const currentDistance = Math.abs(current - referenceInstantMs);
    if (currentDistance < bestDistance) return current;
    if (currentDistance > bestDistance) return best;
    return current < best ? current : best;
  });
}

function resolveGapCompatibleInstant(target: SiteDateTimeParts, timeZone: string): number {
  const utcGuess = Date.UTC(
    target.year,
    target.month - 1,
    target.day,
    target.hours,
    target.minutes,
    0,
    0,
  );
  const targetStamp = toComparableMinuteStamp(target);
  const start = utcGuess - SIX_HOURS_MS;
  const end = utcGuess + SIX_HOURS_MS;

  let nextCandidate: { instantMs: number; delta: number } | null = null;
  let previousCandidate: { instantMs: number; delta: number } | null = null;

  for (let instantMs = start; instantMs <= end; instantMs += ONE_MINUTE_MS) {
    const currentParts = parseSitePartsFromInstant(instantMs, timeZone);
    const currentStamp = toComparableMinuteStamp(currentParts);
    const delta = currentStamp - targetStamp;

    if (delta >= 0) {
      if (
        !nextCandidate ||
        delta < nextCandidate.delta ||
        (delta === nextCandidate.delta && instantMs < nextCandidate.instantMs)
      ) {
        nextCandidate = { instantMs, delta };
      }
      continue;
    }

    if (
      !previousCandidate ||
      delta > previousCandidate.delta ||
      (delta === previousCandidate.delta && instantMs > previousCandidate.instantMs)
    ) {
      previousCandidate = { instantMs, delta };
    }
  }

  return nextCandidate?.instantMs ?? previousCandidate?.instantMs ?? utcGuess;
}

function isValidCalendarDate(year: number, month: number, day: number): boolean {
  const candidate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
  return (
    candidate.getUTCFullYear() === year &&
    candidate.getUTCMonth() === month - 1 &&
    candidate.getUTCDate() === day
  );
}

function assertValidSiteParts(parts: SiteDateTimeParts): void {
  if (!Number.isInteger(parts.year) || parts.year < 1900 || parts.year > 9999) {
    throw new Error(`Invalid year: ${parts.year}`);
  }

  if (!Number.isInteger(parts.month) || parts.month < 1 || parts.month > 12) {
    throw new Error(`Invalid month: ${parts.month}`);
  }

  if (!Number.isInteger(parts.day) || parts.day < 1 || parts.day > 31) {
    throw new Error(`Invalid day: ${parts.day}`);
  }

  if (!isValidCalendarDate(parts.year, parts.month, parts.day)) {
    throw new Error(`Invalid calendar date: ${parts.year}-${parts.month}-${parts.day}`);
  }

  if (!Number.isInteger(parts.hours) || parts.hours < 0 || parts.hours > 23) {
    throw new Error(`Invalid hours: ${parts.hours}`);
  }

  if (!Number.isInteger(parts.minutes) || parts.minutes < 0 || parts.minutes > 59) {
    throw new Error(`Invalid minutes: ${parts.minutes}`);
  }
}

export function utcIsoToSiteDateTimeParts(
  iso: string | null | undefined,
  timeZone = SITE_TIMEZONE,
): SiteDateTimeParts | null {
  if (!iso || !isValidInstantIso(iso)) return null;
  const instantMs = new Date(iso).valueOf();
  const { year, month, day, hours, minutes } = parseSitePartsFromInstant(instantMs, timeZone);
  return { year, month, day, hours, minutes };
}

export function siteDateTimePartsToUtcIso(
  parts: SiteDateTimeParts,
  options?: { timeZone?: string; referenceIso?: string | null },
): string {
  assertValidSiteParts(parts);

  const timeZone = options?.timeZone ?? SITE_TIMEZONE;
  const exactCandidates = getExactCandidateInstants(parts, timeZone);
  const referenceIso = options?.referenceIso;
  const referenceInstantMs =
    referenceIso && isValidInstantIso(referenceIso) ? new Date(referenceIso).valueOf() : undefined;

  const resolvedInstantMs = exactCandidates.length
    ? chooseClosestInstant(exactCandidates, referenceInstantMs)
    : resolveGapCompatibleInstant(parts, timeZone);

  return new Date(resolvedInstantMs).toISOString();
}

export function formatUtcIsoInSiteTimeZone(
  iso: string | null | undefined,
  locale?: string,
  timeZone = SITE_TIMEZONE,
): string {
  if (!iso || !isValidInstantIso(iso)) return '';
  return getLocalizedFormatter('label', locale, timeZone).format(new Date(iso));
}

export function formatUtcIsoTimeInSiteTimeZone(
  iso: string | null | undefined,
  locale?: string,
  timeZone = SITE_TIMEZONE,
): string {
  if (!iso || !isValidInstantIso(iso)) return '';
  return getLocalizedFormatter('time', locale, timeZone).format(new Date(iso));
}

export function formatShiftbookDateTime(
  value: string | null | undefined,
  locale?: string,
  timeZone?: string,
): string {
  if (!value) {
    return '\u2014';
  }

  const formatted = formatUtcIsoInSiteTimeZone(value, locale, timeZone);
  return formatted || value;
}
